import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import { Heading, Page, LayoutSingleColumn, NamedLink } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import Footer from '../../components/Footer/Footer';

import css from './TermsOfServicePage.module.css';

const LEGAL_INFO = {
  marketplaceName: 'DoneThat',
  websiteUrl: 'https://getdonethat.com',
  companyAddress: '3400 Cottage Way, Ste G2 #13236, Sacramento, CA 95825, United States',
  country: 'United States',
  contactEmail: 'hello@getdonethat.com',
  lastUpdated: 'May 04, 2026',
};

const TOC = [
  { id: 'definitions', number: '1', title: 'Interpretation and definitions' },
  { id: 'contact', number: '2', title: 'Contact us' },
  { id: 'acknowledgment', number: '3', title: 'Acknowledgment' },
  { id: 'governing-law', number: '4', title: 'Governing law' },
  { id: 'accounts', number: '5', title: 'User accounts' },
  { id: 'content', number: '6', title: 'Content' },
  { id: 'bookings', number: '7', title: 'Bookings of sessions' },
  { id: 'liability', number: '8', title: 'Disclaimers and limitation of liability' },
  { id: 'disputes', number: '9', title: 'Disputes about the Service' },
  { id: 'ip', number: '10', title: 'Intellectual property of the Service' },
  { id: 'feedback', number: '11', title: 'Your feedback to us' },
  { id: 'changes', number: '12', title: 'Changes to these Terms' },
];

const Term = ({ name, children }) => (
  <>
    <dt className={css.dt}>{name}</dt>
    <dd className={css.dd}>{children}</dd>
  </>
);

/* ============================================================================
   TermsArticleBody — the full article markup. Used by both the standalone page
   and the modal-context content shim consumed by AuthenticationPage.
   ============================================================================ */
const TermsArticleBody = () => (
  <article className={css.article}>
    <p className={css.lead}>
      Please read these Terms of Service carefully before using {LEGAL_INFO.marketplaceName}. By
      accessing or using the Service, you agree to be bound by these Terms.
    </p>

    {/* 1 — Interpretation and Definitions */}
    <section id="definitions" className={css.section}>
      <h2 className={css.h2}>
        <span className={css.h2Number}>1</span> Interpretation and definitions
      </h2>

      <h3 className={css.h3}>1.1 — Interpretation</h3>
      <p className={css.p}>
        The words of which the initial letter is capitalized have meanings defined under the
        following conditions. The following definitions shall have the same meaning regardless of
        whether they appear in the singular or in the plural.
      </p>

      <h3 className={css.h3}>1.2 — Definitions</h3>
      <p className={css.p}>For the purposes of these Terms of Service:</p>
      <dl className={css.dl}>
        <Term name="Account">
          means a unique account created for You to access our Service or parts of our Service.
        </Term>
        <Term name="Affiliate">
          means an entity that controls, is controlled by, or is under common control with a party,
          where &ldquo;control&rdquo; means ownership of 50% or more of the shares, equity interest,
          or other securities entitled to vote for the election of directors or other managing
          authority.
        </Term>
        <Term name="Application">
          means the software program provided by the Company, named {LEGAL_INFO.marketplaceName}.
        </Term>
        <Term name="Booking">
          means a request by You to book a video consultation Session with an Expert through the
          Application or Website.
        </Term>
        <Term name="Company">
          (referred to as either &ldquo;the Company&rdquo;, &ldquo;We&rdquo;, &ldquo;Us&rdquo; or
          &ldquo;Our&rdquo; in this Agreement) refers to {LEGAL_INFO.marketplaceName},{' '}
          {LEGAL_INFO.companyAddress}.
        </Term>
        <Term name="Content">
          refers to content such as text, images, profile information, listings, or other
          information that can be posted, uploaded, linked to, or otherwise made available by You,
          regardless of the form of that content.
        </Term>
        <Term name="Country">refers to {LEGAL_INFO.country}.</Term>
        <Term name="Customer">
          refers to users of the Service who are placing Bookings to receive Sessions from Experts.
        </Term>
        <Term name="Device">
          means any device that can access the Service such as a computer, a cell phone, or a
          digital tablet.
        </Term>
        <Term name="Expert">
          refers to users of the Service who list and provide Sessions to Customers. Experts are
          independent business owners and operators — not employees of the Company.
        </Term>
        <Term name="Feedback">
          means feedback, innovations, or suggestions sent by You regarding the attributes,
          performance, or features of our Service.
        </Term>
        <Term name="Session">
          refers to the paid, time-boxed video consultation offered by an Expert through the Service
          (typically 30 or 60 minutes).
        </Term>
        <Term name="Service">refers to the Application or the Website or both.</Term>
        <Term name="Terms of Service">
          (also referred to as &ldquo;Terms&rdquo;) mean these Terms of Service that form the entire
          agreement between You and the Company regarding the use of the Service.
        </Term>
        <Term name="Third-party Social Media Service">
          means any services or content (including data, information, products, or services)
          provided by a third party that may be displayed, included, or made available by the
          Service.
        </Term>
        <Term name="Website">
          refers to {LEGAL_INFO.marketplaceName}, accessible from {LEGAL_INFO.websiteUrl}.
        </Term>
        <Term name="You">
          means the individual accessing or using the Service, or the company, or other legal entity
          on behalf of which such individual is accessing or using the Service, as applicable.
        </Term>
      </dl>
    </section>

    {/* 2 — Contact us */}
    <section id="contact" className={css.section}>
      <h2 className={css.h2}>
        <span className={css.h2Number}>2</span> Contact us
      </h2>
      <p className={css.p}>
        If you have any questions about these Terms of Service, You can contact us:
      </p>
      <ul className={css.ul}>
        <li className={css.li}>
          By email:{' '}
          <a href={`mailto:${LEGAL_INFO.contactEmail}`} className={css.link}>
            {LEGAL_INFO.contactEmail}
          </a>
        </li>
        <li className={css.li}>
          Through our help center:{' '}
          <NamedLink name="FAQPage" className={css.link}>
            FAQ &amp; Support
          </NamedLink>
        </li>
      </ul>
    </section>

    {/* 3 — Acknowledgment */}
    <section id="acknowledgment" className={css.section}>
      <h2 className={css.h2}>
        <span className={css.h2Number}>3</span> Acknowledgment
      </h2>
      <p className={css.p}>
        These are the Terms of Service governing the use of this Service and the agreement that
        operates between You and the Company. These Terms set out the rights and obligations of all
        users regarding the use of the Service.
      </p>
      <p className={css.p}>
        Your access to and use of the Service is conditioned on Your acceptance of and compliance
        with these Terms. These Terms apply to all visitors, users, and others who access or use
        the Service.
      </p>
      <p className={css.p}>
        By accessing or using the Service You agree to be bound by these Terms. If You disagree with
        any part of these Terms then You may not access the Service.
      </p>
      <p className={css.p}>
        You represent that You are over the age of majority according to the laws of your country
        or the Country, whichever is higher. The Company does not permit those under that age to
        use the Service.
      </p>
      <p className={css.p}>
        Your access to and use of the Service is also conditioned on Your acceptance of and
        compliance with the Privacy Policy of the Company. Our{' '}
        <NamedLink name="PrivacyPolicyPage" className={css.link}>
          Privacy Policy
        </NamedLink>{' '}
        describes Our policies and procedures on the collection, use, and disclosure of Your
        personal information when You use the Service. Please read Our Privacy Policy carefully
        before using Our Service.
      </p>
    </section>

    {/* 4 — Governing Law */}
    <section id="governing-law" className={css.section}>
      <h2 className={css.h2}>
        <span className={css.h2Number}>4</span> Governing law
      </h2>
      <p className={css.p}>
        The laws of the Country, excluding its conflicts of law rules, shall govern these Terms and
        Your use of the Service. Your use of the Application or Website may also be subject to
        other local, state, national, or international laws.
      </p>

      <h3 className={css.h3}>4.1 — For European Union (EU) Users</h3>
      <p className={css.p}>
        If You are a European Union consumer, you will benefit from any mandatory provisions of the
        law of the country in which you are resident.
      </p>

      <h3 className={css.h3}>4.2 — United States legal compliance</h3>
      <p className={css.p}>
        You represent and warrant that (i) You are not located in a country that is subject to the
        United States government embargo, or that has been designated by the United States
        government as a &ldquo;terrorist supporting&rdquo; country, and (ii) You are not listed on
        any United States government list of prohibited or restricted parties.
      </p>

      <h3 className={css.h3}>4.3 — Severability</h3>
      <p className={css.p}>
        If any provision of these Terms is held to be unenforceable or invalid, such provision will
        be changed and interpreted to accomplish the objectives of such provision to the greatest
        extent possible under applicable law and the remaining provisions will continue in full
        force and effect.
      </p>

      <h3 className={css.h3}>4.4 — Waiver</h3>
      <p className={css.p}>
        Except as provided herein, the failure to exercise a right or to require the performance of
        an obligation under these Terms shall not affect a party&apos;s ability to exercise such
        right or require such performance at any time thereafter, nor shall the waiver of a breach
        constitute a waiver of any subsequent breach.
      </p>
    </section>

    {/* 5 — User Accounts */}
    <section id="accounts" className={css.section}>
      <h2 className={css.h2}>
        <span className={css.h2Number}>5</span> User accounts
      </h2>

      <h3 className={css.h3}>5.1 — Account creation</h3>
      <p className={css.p}>
        When You create an account with Us, You must provide Us with information that is accurate,
        complete, and current at all times. Failure to do so constitutes a breach of the Terms,
        which may result in immediate termination of Your account on Our Service.
      </p>
      <p className={css.p}>
        You may not use as a username the name of another person or entity or that is not lawfully
        available for use, a name or trademark that is subject to any rights of another person or
        entity other than You without appropriate authorization, or a name that is otherwise
        offensive, vulgar, or obscene.
      </p>

      <h3 className={css.h3}>5.2 — Account information</h3>
      <p className={css.p}>
        You may be asked to supply certain information relevant to Your Account including, without
        limitation, Your name, email, phone number, and address.
      </p>
      <p className={css.p}>
        Experts may be required to provide additional documentation to comply with identity
        verification, background checks where lawful, and to receive payouts — including bank
        account details and identity documents.
      </p>
      <p className={css.p}>
        Customers may be asked to supply payment card details and billing information at the time
        of Booking. All payment information is provided directly to Our third-party payment
        processor (Stripe) and is not stored on Our servers.
      </p>

      <h3 className={css.h3}>5.3 — Account review</h3>
      <p className={css.p}>
        We perform a vetting process on Experts before listing them on the Service to confirm
        hands-on business operating experience in the niche they advise on. This review is not a
        guarantee of any specific outcome, and We do not endorse any individual Expert. We do not
        accept any responsibility for the reliability, accuracy, and completeness of any
        information provided by users.
      </p>

      <h3 className={css.h3}>5.4 — Account password</h3>
      <p className={css.p}>
        You are responsible for safeguarding the password that You use to access the Service and
        for any activities or actions under Your password, whether Your password is with Our
        Service or a Third-Party Social Media Service.
      </p>
      <p className={css.p}>
        You agree not to disclose Your password to any third party. You must notify Us immediately
        upon becoming aware of any breach of security or unauthorized use of Your account.
      </p>

      <h3 className={css.h3}>5.5 — Account termination</h3>
      <p className={css.p}>
        We may terminate or suspend Your Account immediately, without prior notice or liability,
        for any reason whatsoever, including without limitation if You breach these Terms. Upon
        termination, Your right to use the Service will cease immediately.
      </p>
      <p className={css.p}>
        If You wish to terminate Your Account, You may simply discontinue using the Service or
        delete Your Account from the Service, or contact Us for help.
      </p>
    </section>

    {/* 6 — Content */}
    <section id="content" className={css.section}>
      <h2 className={css.h2}>
        <span className={css.h2Number}>6</span> Content
      </h2>

      <h3 className={css.h3}>6.1 — Your right to post Content</h3>
      <p className={css.p}>
        Our Service allows You to post Content — including profile information, Session listings,
        reviews, messages, and call notes. You are responsible for the Content that You post to the
        Service, including its legality, reliability, and appropriateness.
      </p>
      <p className={css.p}>
        By posting Content to the Service, You grant Us the right and license to use, modify,
        publicly perform, publicly display, reproduce, and distribute such Content on and through
        the Service. You retain any and all of Your rights to any Content You submit, post, or
        display on or through the Service and You are responsible for protecting those rights. You
        agree that this license includes the right for Us to make Your Content available to other
        users of the Service.
      </p>
      <p className={css.p}>
        You represent and warrant that: (i) the Content is Yours (You own it) or You have the right
        to use it and grant Us the rights and license as provided in these Terms, and (ii) the
        posting of Your Content on or through the Service does not violate the privacy rights,
        publicity rights, copyrights, contract rights or any other rights of any person.
      </p>

      <h3 className={css.h3}>6.2 — Content restrictions</h3>
      <p className={css.p}>
        The Company is not responsible for the content of the Service&apos;s users. You expressly
        understand and agree that You are solely responsible for the Content and for all activity
        that occurs under your account, whether done so by You or any third person using Your
        account.
      </p>
      <p className={css.p}>
        You may not transmit any Content that is unlawful, offensive, threatening, libelous,
        defamatory, obscene, or otherwise objectionable. Examples of such objectionable Content
        include, but are not limited to:
      </p>
      <ul className={css.ul}>
        <li className={css.li}>Unlawful or promoting unlawful activity.</li>
        <li className={css.li}>
          Defamatory, discriminatory, or mean-spirited content, including references or commentary
          about religion, race, sexual orientation, gender, national or ethnic origin, or other
          protected groups.
        </li>
        <li className={css.li}>
          Spam, unauthorized solicitations, chain letters, lotteries, or gambling content.
        </li>
        <li className={css.li}>
          Viruses, worms, malware, trojan horses, or other content designed to disrupt, damage, or
          limit the functioning of any software, hardware, or telecommunications equipment.
        </li>
        <li className={css.li}>
          Content infringing on the intellectual property rights of any party.
        </li>
        <li className={css.li}>
          Content impersonating any person or entity, including the Company and its employees or
          representatives.
        </li>
        <li className={css.li}>Content violating the privacy of any third person.</li>
        <li className={css.li}>False information about credentials or experience.</li>
      </ul>
      <p className={css.p}>
        The Company reserves the right, but not the obligation, to determine in its sole discretion
        whether any Content is appropriate and complies with these Terms, and to refuse or remove
        this Content. The Company can also limit or revoke Your use of the Service if You post
        objectionable Content.
      </p>

      <h3 className={css.h3}>6.3 — Content backups</h3>
      <p className={css.p}>
        Although regular backups of Content are performed, the Company does not guarantee there
        will be no loss or corruption of data. You agree to maintain a complete and accurate copy
        of any Content in a location independent of the Service.
      </p>

      <h3 className={css.h3}>
        6.4 — Intellectual property of others and copyright infringement
      </h3>
      <p className={css.p}>
        We respect the intellectual property and copyrights of others. It is Our policy to respond
        to any claim that Content posted on the Service infringes a copyright or other intellectual
        property right of any person. We are ready to comply with local regulations in that matter
        (such as the Digital Millennium Copyright Act (DMCA) or the EU Copyright Directive).
      </p>
      <p className={css.p}>
        If You are a copyright owner or authorized on behalf of one, and You believe that the
        copyrighted work has been copied in a way that constitutes copyright infringement that is
        taking place through the Service, please send written notice to{' '}
        <a href={`mailto:${LEGAL_INFO.contactEmail}`} className={css.link}>
          {LEGAL_INFO.contactEmail}
        </a>{' '}
        including: an electronic or physical signature; a description of the copyrighted work; the
        URL of the allegedly infringing material; Your contact information; a good-faith
        statement; and a statement under penalty of perjury that You are authorized to act.
      </p>
    </section>

    {/* 7 — Bookings */}
    <section id="bookings" className={css.section}>
      <h2 className={css.h2}>
        <span className={css.h2Number}>7</span> Bookings of sessions
      </h2>
      <p className={css.p}>
        By placing a Booking through the Service, You warrant that You are legally capable of
        entering into binding contracts.
      </p>

      <h3 className={css.h3}>7.1 — Position of the Service in Bookings</h3>
      <p className={css.p}>
        Our role is one of a facilitator between You and the Experts. We are a third party in
        Bookings, which limits Our liabilities in any disputes between You and the Experts.
      </p>
      <p className={css.p}>
        We are not a party to any agreement You have with an Expert. Any agreement You enter with
        an Expert does not form a part of any agreement We have with you.
      </p>

      <h3 className={css.h3}>7.2 — Your information as Customer</h3>
      <p className={css.p}>
        If You wish to place a Booking, You may be asked to supply certain information relevant to
        Your Booking — including Your name, email, phone number, payment card details, and billing
        address.
      </p>
      <p className={css.p}>
        You represent and warrant that: (i) You have the legal right to use any credit, debit card,
        or other payment method in connection with any Booking; and (ii) the information You supply
        to us is true, correct, and complete.
      </p>

      <h3 className={css.h3}>7.3 — Availability, errors, and inaccuracies</h3>
      <p className={css.p}>
        Experts and We are constantly updating the offerings on the Service. The information
        available — including pricing, Expert availability, and Session descriptions — may be
        inaccurate or out of date. We reserve the right to change or update information and to
        correct errors, inaccuracies, or omissions at any time without prior notice.
      </p>

      <h3 className={css.h3}>7.4 — Prices</h3>
      <p className={css.p}>
        The Company and Experts reserve the right to revise prices at any time prior to accepting a
        Booking. The price displayed at the time of confirmed Booking is the price You are charged.
      </p>

      <h3 className={css.h3}>7.5 — Payments</h3>
      <p className={css.p}>
        Payments are processed securely through Stripe. Payment cards are subject to validation
        checks and authorization by Your card issuer. If we do not receive the required
        authorization, We will not be liable for any delay or non-completion of Your Booking.
      </p>
      <p className={css.p}>
        Customer payment is held until the Session is delivered. Once the Session completes
        successfully, the Expert&apos;s share is released to them via Stripe Connect, less
        applicable Service Fees.
      </p>

      <h3 className={css.h3}>7.6 — Service fees</h3>
      <p className={css.p}>
        We charge a Service Fee on each Booking to operate and improve the Service. The fee
        structure is displayed during checkout. We reserve the right to change the Service Fees at
        any time, with changes taking effect for new Bookings made after the change.
      </p>

      <h3 className={css.h3}>7.7 — Booking modification</h3>
      <p className={css.p}>
        You and the Expert are responsible for any Booking modifications you agree to make via the
        Service and agree to pay any additional amounts, fees, or taxes associated with any
        modification.
      </p>

      <h3 className={css.h3}>7.8 — Booking cancellation</h3>

      <h4 className={css.h4}>7.8.1 — Our cancellation rights</h4>
      <p className={css.p}>
        We reserve the right to refuse or cancel a Booking at any time for reasons including but
        not limited to: errors in the description or pricing of a Session, fraud or unauthorized
        transactions, an Expert&apos;s account being suspended, or technical issues that prevent
        the Session from being delivered.
      </p>

      <h4 className={css.h4}>7.8.2 — Cancellation by Customers</h4>
      <p className={css.p}>
        Customers may reschedule or cancel a Booking up to 24 hours before the scheduled Session
        for a full refund. Cancellations within 24 hours of the scheduled Session may be subject to
        a cancellation fee, which is retained to compensate the Expert who reserved the time slot.
      </p>

      <h4 className={css.h4}>7.8.3 — Cancellation by Experts</h4>
      <p className={css.p}>
        If an Expert cancels a confirmed Booking, the amount the Customer paid (including the
        Service Fees) will be refunded to the Customer in full. Repeated cancellations by an Expert
        may result in suspension or removal from the Service.
      </p>

      <h4 className={css.h4}>7.8.4 — Satisfaction guarantee</h4>
      <p className={css.p}>
        If an Expert no-shows or the Session is materially impaired through no fault of Yours
        (e.g., poor connection on the Expert&apos;s end, the Expert leaves early without cause),
        You are entitled to a full refund or a free booking credit at Our discretion. To request
        this remedy, contact Us within 7 days of the Session.
      </p>

      <h3 className={css.h3}>7.9 — Booking disputes</h3>
      <p className={css.p}>
        If a Customer or an Expert disputes a Booking, the Company should be notified. The dispute
        will be reviewed and resolved at Our sole discretion based on the facts and any evidence
        the parties provide.
      </p>
    </section>

    {/* 8 — Disclaimers and Liability */}
    <section id="liability" className={css.section}>
      <h2 className={css.h2}>
        <span className={css.h2Number}>8</span> Disclaimers and limitation of liability
      </h2>

      <h3 className={css.h3}>8.1 — Limitation of liability</h3>
      <p className={css.p}>
        Notwithstanding any damages that You might incur, the entire liability of the Company and
        any of its suppliers under any provision of these Terms and Your exclusive remedy for all
        of the foregoing shall be limited to the amount actually paid by You through the Service,
        or 100 USD (or its equivalent in the Service local currency) if You have not paid anything
        through the Service.
      </p>
      <p className={css.p}>
        To the maximum extent permitted by applicable law, in no event shall the Company or its
        suppliers be liable for any special, incidental, indirect, or consequential damages
        whatsoever (including, but not limited to, damages for loss of profits, loss of data or
        other information, business interruption, personal injury, or loss of privacy arising out
        of or in any way related to the use of or inability to use the Service), even if the
        Company has been advised of the possibility of such damages and even if the remedy fails of
        its essential purpose.
      </p>
      <p className={css.p}>
        Some jurisdictions do not allow the exclusion of implied warranties or limitation of
        liability for incidental or consequential damages, which means that some of the above
        limitations may not apply. In these jurisdictions, each party&apos;s liability will be
        limited to the greatest extent permitted by law.
      </p>

      <h3 className={css.h3}>
        8.2 — &ldquo;As-is&rdquo; and &ldquo;as-available&rdquo; disclaimer
      </h3>
      <p className={css.p}>
        The Service is provided to You &ldquo;as is&rdquo; and &ldquo;as available&rdquo; and with
        all faults and defects without warranty of any kind. To the maximum extent permitted under
        applicable law, the Company expressly disclaims all warranties, whether express, implied,
        statutory, or otherwise, including all implied warranties of merchantability, fitness for a
        particular purpose, title, and non-infringement.
      </p>
      <p className={css.p}>
        Advice, recommendations, opinions, and any other information shared by Experts during a
        Session are provided in their personal capacity and do not constitute legal, financial,
        tax, medical, or other professional advice. You should consult a qualified professional
        before acting on any information received through the Service.
      </p>

      <h3 className={css.h3}>8.3 — Links to other websites</h3>
      <p className={css.p}>
        Our Service may contain links to third-party websites or services that are not owned or
        controlled by the Company. The Company has no control over, and assumes no responsibility
        for, the content, privacy policies, or practices of any third-party websites or services.
      </p>

      <h3 className={css.h3}>8.4 — Translation interpretation</h3>
      <p className={css.p}>
        These Terms may have been translated if We have made them available to You on our Service.
        You agree that the original English text shall prevail in the case of a dispute.
      </p>
    </section>

    {/* 9 — Disputes */}
    <section id="disputes" className={css.section}>
      <h2 className={css.h2}>
        <span className={css.h2Number}>9</span> Disputes about the Service
      </h2>
      <p className={css.p}>
        If You have any concerns or disputes about the Service, You agree to first try to resolve
        the dispute informally by contacting the Company at{' '}
        <a href={`mailto:${LEGAL_INFO.contactEmail}`} className={css.link}>
          {LEGAL_INFO.contactEmail}
        </a>
        .
      </p>
    </section>

    {/* 10 — IP */}
    <section id="ip" className={css.section}>
      <h2 className={css.h2}>
        <span className={css.h2Number}>10</span> Intellectual property of the Service
      </h2>
      <p className={css.p}>
        The Service and its original content (excluding Content provided by You or other users),
        features, and functionality are and will remain the exclusive property of the Company and
        its licensors.
      </p>
      <p className={css.p}>
        The Service is protected by copyright, trademark, and other laws of both the Country and
        foreign countries. Our trademarks and trade dress may not be used in connection with any
        product or service without the prior written consent of the Company.
      </p>
    </section>

    {/* 11 — Feedback */}
    <section id="feedback" className={css.section}>
      <h2 className={css.h2}>
        <span className={css.h2Number}>11</span> Your feedback to us
      </h2>
      <p className={css.p}>
        You assign all rights, title, and interest in any Feedback You provide the Company. If for
        any reason such assignment is ineffective, You agree to grant the Company a non-exclusive,
        perpetual, irrevocable, royalty-free, worldwide right and license to use, reproduce,
        disclose, sub-license, distribute, modify, and exploit such Feedback without restriction.
      </p>
    </section>

    {/* 12 — Changes */}
    <section id="changes" className={css.section}>
      <h2 className={css.h2}>
        <span className={css.h2Number}>12</span> Changes to these Terms
      </h2>
      <p className={css.p}>
        We reserve the right, at Our sole discretion, to modify or replace these Terms at any time.
        If a revision is material We will make reasonable efforts to provide at least 30 days
        notice prior to any new terms taking effect. What constitutes a material change will be
        determined at Our sole discretion.
      </p>
      <p className={css.p}>
        By continuing to access or use Our Service after those revisions become effective, You
        agree to be bound by the revised terms. If You do not agree to the new terms, in whole or
        in part, please stop using the Application or Website and the Service.
      </p>
    </section>
  </article>
);

/* ============================================================================
   TermsOfServiceContent — content-only render used inside modals (e.g. the
   signup flow renders this so users can review Terms before agreeing).
   ============================================================================ */
export const TermsOfServiceContent = () => (
  <div className={css.modalContent}>
    <h2 className={css.modalHeading}>
      Terms of <em>service</em>
    </h2>
    <p className={css.modalLastUpdated}>
      Last updated: <strong>{LEGAL_INFO.lastUpdated}</strong>
    </p>
    <TermsArticleBody />
  </div>
);

/* ============================================================================
   Full TermsOfServicePage
   ============================================================================ */
export const TermsOfServicePageComponent = props => {
  const { scrollingDisabled } = props;
  const intl = useIntl();

  const title = intl.formatMessage({
    id: 'TermsOfServicePage.title',
    defaultMessage: 'Terms of Service — DoneThat',
  });

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<Footer />}>
        <div className={css.root}>
          {/* ===== Hero ===== */}
          <header className={css.hero}>
            <div className={css.heroBg} aria-hidden />
            <div className={css.heroInner}>
              <span className={css.eyebrow}>Legal</span>
              <Heading as="h1" rootClassName={css.heroHeading}>
                Terms of <em>service</em>.
              </Heading>
              <p className={css.heroDescription}>
                The agreement between you and {LEGAL_INFO.marketplaceName} when you book or offer a
                video consultation through our marketplace. Please read carefully — using the
                Service means you accept these Terms.
              </p>
              <p className={css.lastUpdated}>
                <span className={css.lastUpdatedDot} aria-hidden /> Last updated:{' '}
                <strong>{LEGAL_INFO.lastUpdated}</strong>
              </p>
            </div>
          </header>

          {/* ===== Body ===== */}
          <div className={css.body}>
            <div className={css.bodyInner}>
              <aside className={css.toc} aria-label="Table of contents">
                <span className={css.tocLabel}>Contents</span>
                <ol className={css.tocList}>
                  {TOC.map(item => (
                    <li key={item.id} className={css.tocItem}>
                      <a href={`#${item.id}`} className={css.tocLink}>
                        <span className={css.tocNumber}>{item.number}</span>
                        <span>{item.title}</span>
                      </a>
                    </li>
                  ))}
                </ol>
              </aside>

              <TermsArticleBody />
            </div>
          </div>

          {/* ===== Bottom help card ===== */}
          <section className={css.helpSection}>
            <div className={css.helpCard}>
              <div>
                <span className={css.helpEyebrow}>Questions about these Terms?</span>
                <h2 className={css.helpHeading}>
                  We&apos;re a real person away from <em>a real answer</em>.
                </h2>
                <p className={css.helpText}>
                  Most questions are already answered in our help center. For anything else, reply
                  fast at{' '}
                  <a href={`mailto:${LEGAL_INFO.contactEmail}`} className={css.helpLink}>
                    {LEGAL_INFO.contactEmail}
                  </a>
                  .
                </p>
              </div>
              <div className={css.helpActions}>
                <NamedLink name="FAQPage" className={css.helpPrimary}>
                  Visit FAQ
                </NamedLink>
                <a href={`mailto:${LEGAL_INFO.contactEmail}`} className={css.helpSecondary}>
                  Email us
                </a>
              </div>
            </div>
          </section>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const EnhancedTermsOfServicePage = props => {
  const intl = useIntl();
  return <TermsOfServicePageComponent intl={intl} {...props} />;
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const TermsOfServicePage = compose(connect(mapStateToProps))(EnhancedTermsOfServicePage);

export default TermsOfServicePage;
